import {
  FieldValue,
  FieldValues,
  getComponentSubtree,
  setComponentSubtree,
  stripComponents,
} from 'features/libra/ontology/ontologyFieldTypes'
import { toPersistedJsonShape } from 'features/libra/ontology/persistedJson'
import isEqual from 'lodash/isEqual'

import { getScopedFieldValues } from './fieldValuesPatch'
import {
  LibraTicket,
  TICKET_UPDATE_FIELD_KEYS,
  UpdateTicketArgs,
} from './libraTicketsApi'
import type { FieldAccessScopePath } from './useScopedFieldAccess'

type TicketPropertyKey = Exclude<
  keyof Omit<UpdateTicketArgs, 'ticketId'>,
  'fieldValues'
>

type TicketPropertyValue = LibraTicket[TicketPropertyKey]

type PropertyChange = {
  kind: 'property'
  key: TicketPropertyKey
  before: TicketPropertyValue
  after: TicketPropertyValue
}

type FieldValueChange = {
  kind: 'fieldValue'
  scope: FieldAccessScopePath
  fieldId: string
  before: FieldValue | undefined
  after: FieldValue | undefined
}

export type PendingTicketChange = PropertyChange | FieldValueChange

export function applyTicketPatch(
  ticket: LibraTicket,
  patch: Omit<UpdateTicketArgs, 'ticketId'>
): LibraTicket {
  const next = { ...ticket }
  for (const key of TICKET_UPDATE_FIELD_KEYS) {
    const value = patch[key]
    if (value !== undefined) Object.assign(next, { [key]: value })
  }
  return next
}

function getTicketChanges(
  before: LibraTicket,
  after: LibraTicket
): PendingTicketChange[] {
  const changes: PendingTicketChange[] = []
  for (const key of TICKET_UPDATE_FIELD_KEYS) {
    if (key === 'fieldValues') continue
    const previous = before[key]
    const value = after[key]
    if (!isJsonEqual(previous, value)) {
      changes.push({
        kind: 'property',
        key,
        before: previous,
        after: value,
      })
    }
  }
  changes.push(...getFieldValueChanges(before.fieldValues, after.fieldValues))
  return changes
}

export function reconcileTicketRefresh(
  incoming: LibraTicket,
  pendingChanges: readonly PendingTicketChange[]
): { ticket: LibraTicket; pendingChanges: PendingTicketChange[] } {
  const observed = pendingChanges.map(() => false)
  // A newest cyclic acknowledgement can equal an older predecessor; inspect
  // the original incoming value before replaying any earlier transition.
  // shortcut: a matching external revert is ambiguous; upgrade when server mutation sequences exist.
  for (let index = 0; index < pendingChanges.length; index += 1) {
    const change = pendingChanges[index]
    if (!isJsonEqual(readChange(incoming, change), change.after)) continue
    for (let earlier = 0; earlier <= index; earlier += 1) {
      if (isSamePath(pendingChanges[earlier], change)) observed[earlier] = true
    }
  }

  let ticket = incoming
  const remaining: PendingTicketChange[] = []

  for (const [index, change] of pendingChanges.entries()) {
    if (observed[index]) {
      const hasUnobservedSuccessor = pendingChanges.some(
        (candidate, candidateIndex) =>
          candidateIndex > index &&
          isSamePath(candidate, change) &&
          !observed[candidateIndex]
      )
      if (hasUnobservedSuccessor) remaining.push(change)
      continue
    }
    const current = readChange(ticket, change)
    if (isJsonEqual(current, change.before)) {
      ticket = applyChange(ticket, change)
      remaining.push(change)
    }
  }

  return { ticket, pendingChanges: remaining }
}

export function commitTicketWrite(
  current: LibraTicket,
  before: LibraTicket,
  after: LibraTicket,
  pendingChanges: readonly PendingTicketChange[]
): { ticket: LibraTicket; pendingChanges: PendingTicketChange[] } {
  let ticket = current
  let nextPending = [...pendingChanges]

  for (const change of getTicketChanges(before, after)) {
    const currentValue = readChange(ticket, change)
    if (isJsonEqual(currentValue, change.before)) {
      ticket = applyChange(ticket, change)
      nextPending.push(change)
    } else {
      nextPending = removePendingChange(nextPending, change)
    }
  }

  return { ticket, pendingChanges: nextPending }
}

function getFieldValueChanges(
  before: FieldValues,
  after: FieldValues
): FieldValueChange[] {
  const scopes: FieldAccessScopePath[] = [{ kind: 'ticket' }]
  const previousComponents = getComponentSubtree(before) ?? {}
  const nextComponents = getComponentSubtree(after) ?? {}
  const componentIds = new Set([
    ...Object.keys(previousComponents),
    ...Object.keys(nextComponents),
  ])
  for (const componentId of componentIds) {
    scopes.push({ kind: 'component', componentId })
  }

  const changes: FieldValueChange[] = []
  for (const scope of scopes) {
    const previous = getScopedFieldValues(before, scope)
    const next = getScopedFieldValues(after, scope)
    const fieldIds = new Set([...Object.keys(previous), ...Object.keys(next)])
    for (const fieldId of fieldIds) {
      const previousValue = Object.hasOwn(previous, fieldId)
        ? previous[fieldId]
        : undefined
      const value = Object.hasOwn(next, fieldId) ? next[fieldId] : undefined
      if (!isJsonEqual(previousValue, value)) {
        changes.push({
          kind: 'fieldValue',
          scope,
          fieldId,
          before: previousValue,
          after: value,
        })
      }
    }
  }
  return changes
}

function readChange(
  ticket: LibraTicket,
  change: PendingTicketChange
): PropertyChange['after'] | FieldValue | undefined {
  if (change.kind === 'property') return ticket[change.key]
  const fields = getScopedFieldValues(ticket.fieldValues, change.scope)
  return Object.hasOwn(fields, change.fieldId)
    ? fields[change.fieldId]
    : undefined
}

function applyChange(
  ticket: LibraTicket,
  change: PendingTicketChange
): LibraTicket {
  if (change.kind === 'property') {
    return Object.assign({}, ticket, { [change.key]: change.after })
  }

  const fieldValues = ticket.fieldValues
  if (change.scope.kind === 'ticket') {
    const topLevel = stripComponents(fieldValues)
    const nextTopLevel = writeFieldValue(topLevel, change.fieldId, change.after)
    const components = getComponentSubtree(fieldValues)
    return {
      ...ticket,
      fieldValues: components
        ? setComponentSubtree(nextTopLevel, components)
        : nextTopLevel,
    }
  }

  const components = getComponentSubtree(fieldValues) ?? {}
  const current = Object.hasOwn(components, change.scope.componentId)
    ? components[change.scope.componentId]
    : {}
  return {
    ...ticket,
    fieldValues: setComponentSubtree(fieldValues, {
      ...components,
      [change.scope.componentId]: writeFieldValue(
        current,
        change.fieldId,
        change.after
      ),
    }),
  }
}

function writeFieldValue(
  fieldValues: FieldValues,
  fieldId: string,
  value: FieldValue | undefined
): FieldValues {
  if (value === undefined) {
    return Object.fromEntries(
      Object.entries(fieldValues).filter(([key]) => key !== fieldId)
    )
  }
  return { ...fieldValues, [fieldId]: value }
}

function removePendingChange(
  pendingChanges: PendingTicketChange[],
  change: PendingTicketChange
): PendingTicketChange[] {
  return pendingChanges.filter((candidate) => !isSamePath(candidate, change))
}

function isSamePath(
  left: PendingTicketChange,
  right: PendingTicketChange
): boolean {
  if (left.kind !== right.kind) return false
  if (left.kind === 'property' && right.kind === 'property') {
    return left.key === right.key
  }
  if (left.kind !== 'fieldValue' || right.kind !== 'fieldValue') return false
  return (
    left.fieldId === right.fieldId &&
    left.scope.kind === right.scope.kind &&
    (left.scope.kind === 'ticket' ||
      (right.scope.kind === 'component' &&
        left.scope.componentId === right.scope.componentId))
  )
}

function isJsonEqual(left: unknown, right: unknown): boolean {
  return isEqual(toPersistedJsonShape(left), toPersistedJsonShape(right))
}
